import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Wrench, Bug, Megaphone, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


import { useTranslation } from 'react-i18next';


const TAG_META = {
  new_feature: { label: 'New Feature', Icon: Sparkles, color: '#10B981' },
  improvement: { label: 'Improvement', Icon: Wrench, color: '#3B82F6' },
  bug_fix: { label: 'Bug Fix', Icon: Bug, color: '#F59E0B' },
  announcement: { label: 'Announcement', Icon: Megaphone, color: '#8B5CF6' },
};

const LAST_SEEN_KEY = 'eili_changelog_last_seen';

export function useChangelogBadge() {
  const [hasNew, setHasNew] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/changelog`);
        const entries = res.data;
        if (!entries.length) return;
        const lastSeen = localStorage.getItem(LAST_SEEN_KEY) || '1970-01-01T00:00:00Z';
        const newest = entries[0]?.created_at;
        if (newest && newest > lastSeen) {
          setHasNew(true);
        }
      } catch { /* ignore */ }
    };
    check();
  }, []);

  const markSeen = useCallback(() => {
    localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
    setHasNew(false);
  }, []);

  return { hasNew, markSeen };
}

export default function WhatsNewModal({ open, onClose }) {
  const {
    t
  } = useTranslation();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/changelog`);
        setEntries(res.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();
    localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 flex flex-col rounded-2xl shadow-2xl overflow-hidden"
        style={{
          background: 'var(--theme-surface)',
          border: '1px solid var(--theme-border)',
          maxHeight: 'min(75vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
        data-testid="whats-new-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--theme-border)' }}>
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--theme-accentLight)' }}>
              <Sparkles className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
            </div>
            <h2 className="font-bold" style={{ color: 'var(--theme-text)', fontSize: '16px' }}>{t('generated.whatsnewmodal_what_s_new_5519')}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 rounded-full" data-testid="whats-new-close">
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--theme-accent)', borderTopColor: 'transparent' }} />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12">
              <p style={{ color: 'var(--theme-textSecondary)', fontSize: '14px' }}>{t('generated.whatsnewmodal_no_updates_yet_5145')}</p>
            </div>
          ) : (
            entries.map((entry) => {
              const tag = TAG_META[entry.tag] || TAG_META.new_feature;
              return (
                <div key={entry.entry_id} className="flex gap-3" data-testid={`whats-new-entry-${entry.entry_id}`}>
                  <div className="shrink-0 mt-0.5">
                    <div className="h-7 w-7 rounded-md flex items-center justify-center" style={{ background: `${tag.color}15` }}>
                      <tag.Icon className="h-3.5 w-3.5" style={{ color: tag.color }} />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold" style={{ color: 'var(--theme-text)', fontSize: '14px' }}>{entry.title}</h3>
                    <p className="mt-1 whitespace-pre-wrap break-words" style={{ color: 'var(--theme-textSecondary)', fontSize: '13px', lineHeight: '1.5' }}>
                      {entry.description}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold" style={{ background: `${tag.color}15`, color: tag.color, fontSize: '10px' }}>
                        {tag.label}
                      </span>
                      <span style={{ color: 'var(--theme-textSecondary)', fontSize: '10px' }}>
                        {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
